import * as Sentry from '@sentry/browser';
import type { BrowserOptions } from '@sentry/browser';
import { isDevelopmentEnv } from './index';
import { getSentryDSN, getMCOEnvironment, getAppName } from './metaTags';
import safeCall from './safeCall';

// Expose sentry to the global scope allowing it to be called via console.
declare global {
  interface Window {
    __SENTRY_RSP__: typeof Sentry;
  }
}
window.__SENTRY_RSP__ = Sentry;

const appName = getAppName();
const environment = getMCOEnvironment();

const baseOptions: BrowserOptions = {
  release: `${appName}@${process.env.REACT_APP_FE_VERSION}`,
  environment,
  integrations: [
    Sentry.captureConsoleIntegration({
      levels: ['error'], // Only capture console.error, not other console methods
    }),
  ],
  // replaysSessionSampleRate: environment === PRODUCTION_ENV ? 0.1 : 0, // https://docs.sentry.io/platforms/javascript/session-replay/
  // replaysOnErrorSampleRate: 0.1,
  // https://docs.sentry.io/platforms/javascript/configuration/filtering/#using-platformidentifier-nameignore-errors-
  // https://github.com/getsentry/sentry-javascript/issues/3440#issuecomment-954602174
  ignoreErrors: ['Non-Error promise rejection captured'],

  beforeSend(event, hint) {
    const value =
      event?.exception?.values?.[0]?.value ||
      event?.message ||
      String(hint?.originalException ?? '');

    const msg = String(value);

    if (/^\[esri\./i.test(msg)) {
      return null;
    }

    if (/Attempted to load a Vector Map, but failed/i.test(msg)) {
      return null;
    }

    return event;
  },
};

// Add DSN only in production environments per Sentry's documentation.
const options: BrowserOptions = isDevelopmentEnv()
  ? baseOptions
  : { ...baseOptions, dsn: getSentryDSN() };

// Initialize sentry the moment this module is executed.
safeCall(() => Sentry.init(options))();

// Tag the FE application name into the global scope.
if (appName) {
  safeCall(() => Sentry.getGlobalScope().setTag('fe-app', appName))();
}

/**
 * Sets a tag in the global scope. Tags are key/value pairs that allow for
 * additional data to be associated with the events being tracked.
 * Good use-cases: tracking language, local timezone, etc.
 * @param key - A value to be used as key for the tag
 * @param value - Value for the tag
 */
export const setGlobalTag = safeCall(
  (key: string, value: string | number | boolean): void => {
    Sentry.getGlobalScope().setTag(key, value);
  },
);

/**
 * User object interface for Sentry scope configuration
 */
export interface SentryUser {
  /** Optional, but expected by Sentry */
  id?: string;
  /** Optional, but expected by Sentry */
  username?: string;
  /** Optional, but expected by Sentry */
  email?: string;
  /** Additional user properties */
  [key: string]: unknown;
}

/**
 * Sets the user object in Sentry's global scope, which is
 * passed along with every request.
 * @param user - User object containing id, username, email, etc.
 */
export const configureUserScope = safeCall((user?: SentryUser): void => {
  if (!user) {
    return;
  }
  Sentry.getGlobalScope().setUser({
    ...user,
  });
});

/**
 * Clears the global scope.
 */
export const clearGlobalScope = safeCall((): void => {
  Sentry.getGlobalScope().clear();
});

/**
 * Captures the given exception and sets the extra properties
 * as part of the scope for the current exception.
 * @param error - The error to capture
 * @param extra - Additional context data
 */
export const captureExceptionWithScope = safeCall(
  (error: Error | string, extra: Record<string, unknown> = {}): void => {
    if (isDevelopmentEnv()) {
      // eslint-disable-next-line no-console
      console.error('[DEV] captureExceptionWithScope:', error, extra);
    }
    Sentry.withScope(scope => {
      Object.keys(extra).forEach(key => {
        scope.setExtra(key, extra[key]);
      });
      Sentry.captureException(error);
    });
  },
);

/**
 * Captures the error without any extra scopes. Passes along
 * any globally defined scopes from initialization.
 * @param error - The error to capture
 */
export const captureException = safeCall((error: Error | string): void => {
  if (isDevelopmentEnv()) {
    // eslint-disable-next-line no-console
    console.error('[DEV] captureException:', error);
  }
  Sentry.captureException(error);
});
